'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

export default function AuditForm() {
  const [formData, setFormData] = useState({
    contactName: '',
    companyName: '',
    email: '',
    phone: '',
    auditType: '',
    certificationNeeded: '',
    complianceStandards: '',
    documentationRequirements: '',
    auditPurpose: '',
    urgency: ''
  });
  const [isSubmitted, setIsSubmitted] = useState(false);

  const auditTypes = [
    'ISO 9001 Certification',
    'Supplier Audit',
    'Quality Documentation',
    'Process Verification',
    'Other'
  ];

  const urgencyLevels = [
    { value: 'standard', label: 'Standard (5-7 days)' },
    { value: 'expedited', label: 'Expedited (2-3 days)' },
    { value: 'urgent', label: 'Urgent (24 hours)' }
  ];

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    console.log('Audit request submitted:', formData);
    setIsSubmitted(true);
  };

  if (isSubmitted) {
    return (
      <div className="bg-white rounded-2xl shadow-2xl p-8 lg:p-12 text-center">
        <div className="w-20 h-20 bg-success/20 rounded-full flex items-center justify-center mx-auto mb-6">
          <Icon name="CheckCircleIcon" size={48} variant="solid" className="text-success" />
        </div>
        <h2 className="text-3xl font-headline font-bold text-foreground mb-4">
          Audit Request Received!
        </h2>
        <p className="text-lg text-muted-foreground mb-8">
          Thank you for your audit request. Our quality team will prepare the requested documentation and respond within 24 hours.
        </p>
        <button
          onClick={() => setIsSubmitted(false)}
          className="px-8 py-3 bg-accent text-white rounded-lg font-cta font-bold hover:opacity-90 transition-all duration-base"
        >
          Submit Another Request
        </button>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl shadow-2xl p-8 lg:p-12">
      <div className="mb-8">
        <div className="flex items-center space-x-3 mb-4">
          <div className="w-12 h-12 bg-accent/10 rounded-xl flex items-center justify-center">
            <Icon name="ShieldCheckIcon" size={24} variant="solid" className="text-accent" />
          </div>
          <h2 className="text-3xl font-headline font-bold text-foreground">
            Audit Request
          </h2>
        </div>
        <p className="text-muted-foreground font-body">
          Request compliance documentation, certifications, and audit materials for quality assurance and verification.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid md:grid-cols-2 gap-6">
          <div className="space-y-2">
            <label className="block text-sm font-cta font-semibold text-foreground">
              Contact Name <span className="text-error">*</span>
            </label>
            <input
              type="text"
              required
              value={formData.contactName}
              onChange={(e) => setFormData({ ...formData, contactName: e.target.value })}
              placeholder="Your full name"
              className="w-full px-4 py-3 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent transition-all duration-base"
            />
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-cta font-semibold text-foreground">
              Company Name <span className="text-error">*</span>
            </label>
            <input
              type="text"
              required
              value={formData.companyName}
              onChange={(e) => setFormData({ ...formData, companyName: e.target.value })}
              placeholder="Your company name"
              className="w-full px-4 py-3 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent transition-all duration-base"
            />
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-cta font-semibold text-foreground">
              Email Address <span className="text-error">*</span>
            </label>
            <input
              type="email"
              required
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              placeholder="your.email@company.com"
              className="w-full px-4 py-3 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent transition-all duration-base"
            />
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-cta font-semibold text-foreground">
              Phone Number <span className="text-error">*</span>
            </label>
            <input
              type="tel"
              required
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              placeholder="(555) 123-4567"
              className="w-full px-4 py-3 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent transition-all duration-base"
            />
          </div>
        </div>

        <div className="space-y-2">
          <label className="block text-sm font-cta font-semibold text-foreground">
            Audit Type <span className="text-error">*</span>
          </label>
          <select
            required
            value={formData.auditType}
            onChange={(e) => setFormData({ ...formData, auditType: e.target.value })}
            className="w-full px-4 py-3 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent transition-all duration-base"
          >
            <option value="">Select audit type</option>
            {auditTypes.map((type) => (
              <option key={type} value={type}>
                {type}
              </option>
            ))}
          </select>
        </div>

        <div className="space-y-2">
          <label className="block text-sm font-cta font-semibold text-foreground">
            Certification Needed
          </label>
          <input
            type="text"
            value={formData.certificationNeeded}
            onChange={(e) => setFormData({ ...formData, certificationNeeded: e.target.value })}
            placeholder="e.g., ISO 9001:2015, AS9100D"
            className="w-full px-4 py-3 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent transition-all duration-base"
          />
        </div>

        <div className="space-y-2">
          <label className="block text-sm font-cta font-semibold text-foreground">
            Compliance Standards <span className="text-error">*</span>
          </label>
          <textarea
            required
            value={formData.complianceStandards}
            onChange={(e) => setFormData({ ...formData, complianceStandards: e.target.value })}
            placeholder="List the compliance standards and requirements you need to verify..."
            rows={3}
            className="w-full px-4 py-3 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent transition-all duration-base resize-none"
          />
        </div>

        <div className="space-y-2">
          <label className="block text-sm font-cta font-semibold text-foreground">
            Documentation Requirements <span className="text-error">*</span>
          </label>
          <textarea
            required
            value={formData.documentationRequirements}
            onChange={(e) => setFormData({ ...formData, documentationRequirements: e.target.value })}
            placeholder="Specify the documents you need (e.g., quality manual, process procedures, test reports, certificates)..."
            rows={4}
            className="w-full px-4 py-3 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent transition-all duration-base resize-none"
          />
        </div>

        <div className="space-y-2">
          <label className="block text-sm font-cta font-semibold text-foreground">
            Audit Purpose
          </label>
          <input
            type="text"
            value={formData.auditPurpose}
            onChange={(e) => setFormData({ ...formData, auditPurpose: e.target.value })}
            placeholder="e.g., Supplier qualification, customer requirement, internal review"
            className="w-full px-4 py-3 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent transition-all duration-base"
          />
        </div>

        <div className="space-y-2">
          <label className="block text-sm font-cta font-semibold text-foreground">
            Urgency Level <span className="text-error">*</span>
          </label>
          <div className="grid md:grid-cols-3 gap-3">
            {urgencyLevels.map((level) => (
              <button
                key={level.value}
                type="button"
                onClick={() => setFormData({ ...formData, urgency: level.value })}
                className={`p-4 rounded-lg border-2 transition-all duration-base ${
                  formData.urgency === level.value
                    ? 'border-accent bg-accent/5' :'border-border hover:border-accent/50'
                }`}
              >
                <div className="text-sm font-cta font-semibold text-foreground text-center">
                  {level.label}
                </div>
              </button>
            ))}
          </div>
        </div>

        <div className="bg-accent/10 rounded-xl p-6 border border-accent/20">
          <div className="flex items-start space-x-3">
            <Icon name="DocumentCheckIcon" size={24} variant="solid" className="text-accent flex-shrink-0" />
            <div className="space-y-2">
              <div className="text-sm font-cta font-semibold text-foreground">
                Secure Documentation Delivery
              </div>
              <div className="text-sm text-muted-foreground font-body">
                All audit documentation will be delivered through secure channels with proper confidentiality agreements in place.
              </div>
            </div>
          </div>
        </div>

        <button
          type="submit"
          className="w-full px-8 py-4 bg-accent text-white rounded-lg font-cta font-bold hover:opacity-90 transition-all duration-base shadow-lg flex items-center justify-center space-x-2"
        >
          <Icon name="ClipboardDocumentCheckIcon" size={20} variant="solid" />
          <span>Submit Audit Request</span>
        </button>
      </form>
    </div>
  );
}